import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AMLAdjustmentReason } from 'api/Clients/Types/AMLAdjustmentReason'
import { ClientDuplicateDto } from 'api/Clients/Types/ClientDuplicateDto'
import { ClientLeverageType } from 'api/Clients/Types/ClientLeverageType'
import { ClientRelationsDto } from 'api/Clients/Types/ClientRelationDto'
import { Language } from 'api/Clients/Types/Language'
import { StatusReason } from 'api/Clients/Types/StatusReason'
import { TickmillProductDto } from 'api/Clients/Types/TickmillProductDto'
import { NameDto } from 'api/Common/Types'
import stringComparator from 'utils/stringComparator'

export interface ClientState {
  types: NameDto[]
  companyTypes: NameDto[]
  gender: NameDto[]
  nciType: NameDto[]
  education: NameDto[]
  documentType: NameDto[]
  businessNature: NameDto[]
  language: Language[]
  classification: NameDto[]
  marketDataClassification: NameDto[]
  tickmillCompanies: NameDto[]
  status: NameDto[]
  reason: StatusReason[]
  truliooEidStatus: NameDto[]
  truliooWatchlistStatus: NameDto[]
  truliooDocVStatus: NameDto[]
  worldCheckStatus: NameDto[]
  salesStatus: NameDto[]
  leadSalesStatus: NameDto[]
  leverageType: ClientLeverageType[]
  duplicates: ClientDuplicateDto[]
  relations: ClientRelationsDto[]
  restrictions: NameDto[]
  legalBasesTypes: NameDto[]
  ownershipStructures: NameDto[]
  amlCategories: NameDto[]
  adjustmentReasons: AMLAdjustmentReason[]
  taxAvailability: NameDto[]
  tickmillProducts: TickmillProductDto[]
  appropriatenessTestCategories: NameDto[]
  appropriatenessTestSubCategories: NameDto[]
  complianceVulnerabilityGroups: NameDto[]
  onboardingFlowTypes: NameDto[]
  kycProviders: NameDto[]
}

const initialState: ClientState = {
  types: [],
  companyTypes: [],
  gender: [],
  nciType: [],
  education: [],
  documentType: [],
  businessNature: [],
  language: [],
  classification: [],
  marketDataClassification: [],
  tickmillCompanies: [],
  status: [],
  reason: [],
  truliooEidStatus: [],
  truliooWatchlistStatus: [],
  truliooDocVStatus: [],
  worldCheckStatus: [],
  salesStatus: [],
  leadSalesStatus: [],
  leverageType: [],
  duplicates: [],
  relations: [],
  restrictions: [],
  legalBasesTypes: [],
  ownershipStructures: [],
  taxAvailability: [],
  tickmillProducts: [],
  appropriatenessTestCategories: [],
  appropriatenessTestSubCategories: [],
  complianceVulnerabilityGroups: [],
  amlCategories: [],
  adjustmentReasons: [],
  onboardingFlowTypes: [],
  kycProviders: [],
}

// TODO: Delete all NameDto clones as they are useless

export const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    setClientTypes: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.types = action.payload
    },
    setClientCompanyTypes: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.companyTypes = action.payload
    },
    setClientStatus: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.status = action.payload
    },
    setClientStatusReason: (state: ClientState, action: PayloadAction<StatusReason[]>) => {
      state.reason = action.payload
    },
    setClientWorldCheckStatus: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.worldCheckStatus = action.payload
    },
    setClientTruliooEidStatus: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.truliooEidStatus = action.payload
    },
    setClientTruliooWatchlistStatus: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.truliooWatchlistStatus = action.payload
    },
    setClientTruliooDocVStatus: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.truliooDocVStatus = action.payload
    },
    setClientSalesStatus: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.salesStatus = action.payload
    },
    setLeadSalesStatus: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.leadSalesStatus = action.payload
    },
    setClientGender: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.gender = action.payload
    },
    setClientNciType: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.nciType = action.payload
    },
    setClientEducation: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.education = action.payload
    },
    setClientDocumentType: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.documentType = action.payload
    },
    setOnboardingFlowType: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.onboardingFlowTypes = action.payload
    },
    setClientBusinessNature: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.businessNature = action.payload
    },
    setClientLanguages: (state: ClientState, action: PayloadAction<NameDto<string>[]>) => {
      state.language = action.payload.sort((a, b) => stringComparator(a.name, b.name))
    },
    setClientClassificationType: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.classification = action.payload
    },
    setClientMarketDataClassificationType: (
      state: ClientState,
      action: PayloadAction<NameDto[]>
    ) => {
      state.marketDataClassification = action.payload
    },
    setClientTickmillCompanies: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.tickmillCompanies = action.payload
    },
    setClientLeverageType: (state: ClientState, action: PayloadAction<ClientLeverageType[]>) => {
      state.leverageType = action.payload
    },
    setClientPossibleDuplicate: (
      state: ClientState,
      action: PayloadAction<ClientDuplicateDto[]>
    ) => {
      state.duplicates = action.payload
    },
    setClientPossibleRelations: (
      state: ClientState,
      action: PayloadAction<ClientRelationsDto[]>
    ) => {
      state.relations = action.payload
    },
    setClientRestrictions: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.restrictions = action.payload
    },
    setClientLegalBasesTypes: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.legalBasesTypes = action.payload
    },
    setClientCorporateOwnershipStructuresTypes: (
      state: ClientState,
      action: PayloadAction<NameDto[]>
    ) => {
      state.ownershipStructures = action.payload
    },
    setClientTaxAvailabilityStatuses: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.taxAvailability = action.payload
    },
    setClientTickmillProducts: (
      state: ClientState,
      action: PayloadAction<TickmillProductDto[]>
    ) => {
      state.tickmillProducts = action.payload
    },
    setClientAppropriatenessTestCategories: (
      state: ClientState,
      action: PayloadAction<NameDto[]>
    ) => {
      state.appropriatenessTestCategories = action.payload
    },
    setClientAppropriatenessTestSubCategories: (
      state: ClientState,
      action: PayloadAction<NameDto[]>
    ) => {
      state.appropriatenessTestSubCategories = action.payload
    },
    setComplianceVulnerabilityGroups: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.complianceVulnerabilityGroups = action.payload
    },
    setClientAMLCategories: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.amlCategories = action.payload
    },
    setClientAMLAdjustmentReasons: (
      state: ClientState,
      action: PayloadAction<AMLAdjustmentReason[]>
    ) => {
      state.adjustmentReasons = action.payload
    },
    setClientKYCProviders: (state: ClientState, action: PayloadAction<NameDto[]>) => {
      state.kycProviders = action.payload
    },
  },
})
